// Third-party
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Redirect } from 'react-router';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import debounce from 'lodash/debounce';

// Components
import ConnectedPrivateComponent from '../../components/PrivateComponent';
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import { LoadingSpinner } from '../../components/common';
import { PrimaryButton, SecondaryLink } from '../../components/buttons';
import { OverlappingBackground, Header } from '../../components/layout';
import SearchInput from '../../components/inputs/SearchInput';
import { EditContactModal, DeleteContactsModal } from '../../components/modals';
import { ContactsTable } from '../../components/tables';
import RateLimitedComponent from '../../components/RateLimitedComponent';

// Enums
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { getMyContacts, deleteContacts, loadContactForEdit, resetEditContact, setPersistenceSource, GET_CONTACTS } from '../../../redux/modules/contacts';
import { RateLimitType } from '../../../redux/modules/types/rate-limits';
import { incrementLimit, consumeLimit } from '../../../redux/modules/rate-limits';
import { selectIsLoadingEvery, startLoaderAction } from '../../../redux/modules/ui/loaders';
import { setIsActiveContactPostMessage } from '@/redux/modules/post-message';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import notification from '../../../utils/notification';
import { PERMISSIONS } from '../../../utils/permissions';
import { getContactLayout, CONTACT_LAYOUT } from '../../../utils/contacts';
import { showError } from '../../../utils/errors';
import { NAV_PATHS } from '../../../utils/constants';
import sessionStorage, { SessionStorageKey } from '../../../utils/session-storage';
import localStorageUtils from '@/utils/local-storage';
import { getFilteredDataSourceTabs } from '@/utils/data-source';

// Types
import GenericContact from '@/types/generic-contact';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import User from '@/types/user';
import Portfolio from '@/types/portfolio';
import Activity from '@/types/activity';
import Comparison from '@/types/comparison';
import ContactRiskProfile from '@/types/contact-risk-profile';
import AppMode from '@/types/app-mode';
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { SortingField } from '../../../types/data';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  deleteContacts: (contacts: GenericContact[]) => Promise<void>;
  loadContactForEdit: (contact: GenericContact) => void;
  resetEditContact: () => void;
  incrementLimit: (limitType: RateLimitType, count?: number) => void;
  consumeLimit: (limitType: RateLimitType) => void;
  startLoaderAction: (name: string) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
};

type StateProps = {
  user?: User | null;
  locale: string;
  editedContact: Partial<GenericContact>;
  questionnaires: KycQuestionnaire[];
  currentQuestionnaire?: KycQuestionnaire;
  contacts: GenericContact[];
  nextContactsToken?: string;
  loading: {
    contacts: boolean;
    current: {
      portfolios: boolean;
      comparisons: boolean;
      activityLogs: boolean;
    };
  };
  portfolio: Portfolio;
  current?: {
    contact: GenericContact;
    activityLogs?: Activity[];
    portfolios?: Portfolio[];
    nextContactPortfoliosToken?: string;
    nextContactComparisonsToken?: string;
    comparisons?: Comparison[];
    riskProfiles?: ContactRiskProfile[];
    hasMore: {
      comparisons: boolean;
      portfolios: boolean;
    };
    nextActivityLogToken?: string;
  };
  persistenceSource?: string;
  isPostMessageActive: boolean;
};

type Props = RouteComponentProps & DispatchProps & StateProps;

type State = {
  activeDataSource: string;
  redirect?: string;
  searchQuery?: string;
  showEditContactModal: boolean;
  showDeleteContactsModal: boolean;
  selectedContactsForDelete: GenericContact[];
  contactsSortingFields: SortingField[];
  isNewContact?: boolean;
};

class Contacts extends React.Component<Props, State> {
  private contactsTable = React.createRef<ContactsTable>();

  private dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(
    this.props.user,
    DATA_SOURCE_TAB_CATEGORY.CONTACTS,
    this.props.isPostMessageActive,
  );

  readonly state: State = {
    activeDataSource: this.dataSourceTabs?.[0]?.value ?? '',
    searchQuery: '',
    selectedContactsForDelete: [],
    showEditContactModal: false,
    showDeleteContactsModal: false,
    contactsSortingFields: [],
    isNewContact: false,
  };

  debouncedSearchContacts = debounce(() => {
    const { searchQuery, contactsSortingFields } = this.state;
    this.props.getMyContacts({ query: searchQuery, nextToken: undefined, sort: contactsSortingFields });
  }, 250);

  componentDidMount() {
    const { user, getMyContacts, isPostMessageActive, setIsActiveContactPostMessage } = this.props;

    if (user?.appConfig.mode === AppMode.CONTACT) {
      const contactId = sessionStorage.getItem(SessionStorageKey.CONTACT_ID);
      if (contactId != null) {
        const redirect = NAV_PATHS.CONTACT_DETAILS.replace(':contactId', contactId);
        this.props.history.push(redirect);
      }
    }

    if (user) {
      // Reset the Persistence Source when the component mounts
      const isPostMessageDataSourceTabExist = this.dataSourceTabs?.find(tab => tab.isPostMessage);
      const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

      if (isInitialDataSourceTabPostMessage) {
        setIsActiveContactPostMessage(false);
      }
      resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, user);
    }

    getMyContacts();
  }

  showDeleteContactsModal = (contacts: GenericContact[]) => {
    this.setState({
      showDeleteContactsModal: true,
      selectedContactsForDelete: contacts,
    });
  };

  onContactsSearchChange = (searchQuery: string) => {
    this.setState({ searchQuery });
    this.props.startLoaderAction(GET_CONTACTS.type);
    this.debouncedSearchContacts();
  };

  onAddContact = () => {
    this.props.resetEditContact();
    this.setState({ isNewContact: true });
    this.showEditContactModal();
  };

  editContact = (contact: GenericContact) => {
    this.props.loadContactForEdit(contact);

    if (this.state.isNewContact) {
      this.setState({ isNewContact: false });
    }

    this.showEditContactModal();
  };

  onDeleteContacts = () => {
    const { selectedContactsForDelete } = this.state;
    this.props
      .deleteContacts(selectedContactsForDelete)
      .then(() => {
        this.props.incrementLimit(RateLimitType.CONTACTS, selectedContactsForDelete.length);
        notification.success(formatMessage(`Contacts.DeleteModal.${selectedContactsForDelete.length > 1 ? 'DeleteManySuccess' : 'DeleteOneSuccess'}`));
      })
      .catch(showError);

    this.setState({ selectedContactsForDelete: [] });
    this.hideDeleteContactsModal();

    if (this.contactsTable.current) {
      this.contactsTable.current.clearSelection();
    }
  };

  showEditContactModal = () => {
    this.setState({ showEditContactModal: true });
  };

  hideEditContactModal = () => {
    this.setState({ showEditContactModal: false });
  };

  hideDeleteContactsModal = () => {
    this.setState({ showDeleteContactsModal: false });
  };

  onContactSave = (contact: GenericContact) => {
    this.hideEditContactModal();

    if (this.state.isNewContact) {
      this.props.consumeLimit(RateLimitType.CONTACTS);
      this.redirectToContactPage(contact);
    }
  };

  redirectToContactPage = (contact: GenericContact) => {
    if (contact._id) {
      const redirect = NAV_PATHS.CONTACT_DETAILS.replace(':contactId', contact._id);

      this.props.history.push(redirect);
    }
  };

  onContactClick = (contact: GenericContact) => {
    this.redirectToContactPage(contact);
  };

  onContactsSortingChange = (contactsSortingFields: SortingField[]) => {
    const { searchQuery } = this.state;

    this.setState({ contactsSortingFields });
    this.props.getMyContacts({ query: searchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  onLoadMoreContacts = () => {
    const { searchQuery, contactsSortingFields } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: searchQuery, nextToken: nextContactsToken, sort: contactsSortingFields });
  };

  onSelectDataSource = (dataSourceValue: string) => {
    const { user, getMyContacts, setIsActiveContactPostMessage, setPersistenceSource } = this.props;
    const { searchQuery, contactsSortingFields } = this.state;

    if (!user || !this.dataSourceTabs) return;

    this.setState({ activeDataSource: dataSourceValue });

    const activeDataSource = this.dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActiveContactPostMessage(true);
    } else {
      setIsActiveContactPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, persistenceSource);
    getMyContacts({ query: searchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  render() {
    const { loading, user, contacts, editedContact, persistenceSource, isPostMessageActive, setIsActiveContactPostMessage, setPersistenceSource } = this.props;
    const { activeDataSource, redirect, selectedContactsForDelete, showDeleteContactsModal, searchQuery, showEditContactModal } = this.state;

    if (redirect) {
      return <Redirect push to={redirect} />;
    }

    if (!user) {
      return null;
    }

    const layout = getContactLayout(user);

    const searchPlaceholder =
      layout === CONTACT_LAYOUT.CLIENT ? formatMessage('Contacts.Search.PlaceholderClient') : formatMessage('Contacts.Search.Placeholder');

    const pageTitle = layout === CONTACT_LAYOUT.CLIENT ? 'Contacts.PageTitleClient' : 'Contacts.PageTitle';
    const addContactText = layout === CONTACT_LAYOUT.CLIENT ? 'Contacts.AddClient' : 'Contacts.AddContact';

    const hasPersistenceKeySource = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) != null;

    return (
      <>
        <Header />
        <OverlappingBackground />
        <main id="content" className="contacts">
          <div className="contacts-navigation d-flex align-items-center">
            <h1 className="flex-grow-1 h3">
              <FormattedMessage id={pageTitle} />
            </h1>
            {this.dataSourceTabs && (
              <div className="mt-3 me-5">
                <DataSourceTabs disabled={loading.contacts} tabs={this.dataSourceTabs} value={activeDataSource} onChange={this.onSelectDataSource} />
              </div>
            )}
            <SearchInput
              className="contacts-search-input"
              onChange={this.onContactsSearchChange}
              placeholder={searchPlaceholder}
              value={searchQuery}
              data-private
              data-dd-privacy="mask"
            />

            <ConnectedPrivateComponent forcedFalse={hasPersistenceKeySource} permissions={[PERMISSIONS.CONTACTS_CREATE]}>
              {() => (
                <RateLimitedComponent type={RateLimitType.CONTACTS}>
                  {({ disabled, error }) => (
                    <PrimaryButton disabled={disabled} error={error} onClick={this.onAddContact}>
                      <i className="icon-plus" />
                      <FormattedMessage id={addContactText} />
                    </PrimaryButton>
                  )}
                </RateLimitedComponent>
              )}
            </ConnectedPrivateComponent>
          </div>

          {loading.contacts && <LoadingSpinner message={formatMessage('Contacts.Loading')} />}

          {!loading.contacts && contacts.length === 0 && (
            <div className="empty-contacts">
              <h1>{searchQuery ? <FormattedMessage id="Contacts.NoResults.Title" /> : <FormattedMessage id="Contacts.NoData.Title" />}</h1>

              <span className="empty-contacts-text">
                {searchQuery ? <FormattedMessage id="Contacts.NoResults.Text" /> : <FormattedMessage id="Contacts.NoData.Message" />}
              </span>

              {!searchQuery && (
                <SecondaryLink className="add-contact-link" onClick={this.onAddContact}>
                  <FormattedMessage id="Contacts.NoData.GetStarted" />
                  <i className="icon-arrow-up-right2" />
                </SecondaryLink>
              )}
            </div>
          )}

          {contacts.length > 0 && (
            <ContactsTable
              ref={this.contactsTable}
              multiselect
              searchable={false}
              contacts={contacts}
              className={classNames({ hidden: loading.contacts })}
              onClick={this.onContactClick}
              onDelete={this.showDeleteContactsModal}
              onEdit={this.editContact}
              user={user}
              layout={layout}
              onSortingChange={this.onContactsSortingChange}
              onLoadMore={this.onLoadMoreContacts}
              showDataSourceTabs={false}
              debouncedSearchContacts={this.debouncedSearchContacts}
              hasMore={this.props.nextContactsToken != null}
              tableScrollAncestor=".app-scrollbar-container"
              persistenceSource={persistenceSource}
              isPostMessageActive={isPostMessageActive}
              setPersistenceSource={setPersistenceSource}
              setIsActiveContactPostMessage={setIsActiveContactPostMessage}
            />
          )}
        </main>

        <EditContactModal
          user={user}
          show={showEditContactModal}
          onCancel={this.hideEditContactModal}
          onSave={this.onContactSave}
          contact={editedContact}
          isEditing
        />

        <DeleteContactsModal
          contacts={selectedContactsForDelete}
          show={showDeleteContactsModal}
          onCancel={this.hideDeleteContactsModal}
          onConfirm={this.onDeleteContacts}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  contacts: state.contacts.contacts,
  persistenceSource: state.contacts.persistenceSource,
  nextContactsToken: state.contacts.nextContactsToken,
  loading: {
    ...state.contacts.loading,
    contacts: selectIsLoadingEvery(state, [GET_CONTACTS.type]),
  },
  current: state.contacts.current,
  locale: state.intl.locale,
  questionnaires: state.kycQuestionnaire.questionnaires,
  currentQuestionnaire: state.kycQuestionnaire.currentQuestionnaire,
  user: state.auth.user,
  portfolio: state.editPortfolio.portfolio,
  editedContact: state.contacts.editContact,
  isPostMessageActive: state.postMessage.isPostMessageActive,
});

export default withRouter(
  connect(mapStateToProps, {
    getMyContacts,
    deleteContacts,
    loadContactForEdit,
    resetEditContact,
    incrementLimit,
    consumeLimit,
    startLoaderAction,
    setIsActiveContactPostMessage,
    setPersistenceSource,
  })(Contacts),
);
